/**
 * Deeply replace *any* occurrence of `null` with `undefined` throughout T.
 */
export type NullToUndefinedDeep<T> =
  // 1) Union-friendly: this distributes over unions so `null` members become `undefined`
  T extends null
    ? undefined
    : // 2) Functions & constructors: transform params and return/instance types
      T extends (...args: infer A) => infer R
      ? (
          ...args: { [I in keyof A]: NullToUndefinedDeep<A[I]> }
        ) => NullToUndefinedDeep<R>
      : T extends abstract new (...args: infer A) => infer R
        ? abstract new (
            ...args: { [I in keyof A]: NullToUndefinedDeep<A[I]> }
          ) => NullToUndefinedDeep<R>
        : // 3) Tuples & arrays (readonly-safe), preserves tuple-ness and readonly modifiers
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          T extends readonly any[]
          ? { [I in keyof T]: NullToUndefinedDeep<T[I]> }
          : // 4) Common container types
            T extends Promise<infer U>
            ? Promise<NullToUndefinedDeep<U>>
            : T extends Map<infer K, infer V>
              ? Map<NullToUndefinedDeep<K>, NullToUndefinedDeep<V>>
              : T extends ReadonlyMap<infer K, infer V>
                ? ReadonlyMap<NullToUndefinedDeep<K>, NullToUndefinedDeep<V>>
                : T extends Set<infer U>
                  ? Set<NullToUndefinedDeep<U>>
                  : T extends ReadonlySet<infer U>
                    ? ReadonlySet<NullToUndefinedDeep<U>>
                    : // 5) Objects: recurse through properties (optional/readonly preserved)
                      T extends object
                      ? { [K in keyof T]: NullToUndefinedDeep<T[K]> }
                      : // 6) Primitives & everything else: leave as-is
                        T

// ---
export function toNullToUndefinedDeep<T>(value: T): NullToUndefinedDeep<T> {
  if (value === null) {
    return undefined as NullToUndefinedDeep<T>
  }

  if (Array.isArray(value)) {
    return value.map((v) => toNullToUndefinedDeep(v)) as NullToUndefinedDeep<T>
  }

  if (value instanceof Map) {
    return new Map(
      Array.from(value.entries()).map(([k, v]) => [
        toNullToUndefinedDeep(k),
        toNullToUndefinedDeep(v),
      ])
    ) as NullToUndefinedDeep<T>
  }

  if (value instanceof Set) {
    return new Set(
      Array.from(value).map((v) => toNullToUndefinedDeep(v))
    ) as NullToUndefinedDeep<T>
  }

  if (value instanceof Promise) {
    return value.then((v) => toNullToUndefinedDeep(v)) as NullToUndefinedDeep<T>
  }

  if (typeof value === 'object') {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const result: any = {}
    for (const [k, v] of Object.entries(value)) {
      result[k] = toNullToUndefinedDeep(v)
    }
    return result
  }

  return value as NullToUndefinedDeep<T>
}
